import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

/*
 * Campo di ricerca con l'elenco dei risultati (combobox), da usare quando si sceglie una voce fra tante.
 *
 * - Chiuso: mostra la voce scelta con, accanto in grigio, le sue informazioni (`dettaglio`).
 * - Clic: il nome diventa subito testo modificabile (già selezionato: scrivendo si sostituisce) e
 *   l'elenco si apre.
 * - Scrivendo l'elenco si filtra (su etichetta, dettaglio e valore); le lettere trovate sono in grassetto.
 * - La X dentro il campo svuota il testo e lascia in ricerca, con l'elenco aperto.
 * - Tastiera: frecce, Invio sceglie, Esc annulla e torna alla voce di prima.
 *
 * - minCaratteri (default 0): per le liste lunghe (regola «Aggiungi con molte voci») l'elenco non si apre intero:
 *   compare solo dopo i primi caratteri scritti, e mostra al massimo `massimo` risultati.
 *
 * opzioni: [{ value, label, dettaglio? }]   valore: value scelto   onChange(value | '')
 */
const normalizza = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const Evidenzia = ({ testo, cerca }) => {
    const q = normalizza(cerca).trim();
    if (!q) return testo;
    const i = normalizza(testo).indexOf(q);
    if (i < 0) return testo;
    return <>{testo.slice(0, i)}<strong>{testo.slice(i, i + q.length)}</strong>{testo.slice(i + q.length)}</>;
};

export const CampoRicerca = ({ id, opzioni, valore, onChange, nessunRisultato = 'Nessun risultato', etichettaSvuota = 'Svuota la ricerca', minCaratteri = 0, massimo = 0 }) => {
    const idBase = useId();
    const idCampo = id || `ricerca-${idBase}`;
    const idElenco = `${idCampo}-elenco`;
    const campo = useRef(null);
    const elenco = useRef(null);
    const scelta = opzioni.find((o) => o.value === valore) || null;

    const [aperto, setAperto] = useState(false);
    const [testo, setTesto] = useState('');
    const [attiva, setAttiva] = useState(0);

    // Testo uguale alla voce scelta = nessun filtro: si vede tutto l'elenco
    const filtro = scelta && testo === scelta.label ? '' : testo;
    const risultati = useMemo(() => {
        const q = normalizza(filtro).trim();
        if (q.length < minCaratteri) return [];
        if (!q) return opzioni;
        const nelNome = opzioni.filter((o) => normalizza(o.label).includes(q));
        const altrove = opzioni.filter((o) => !normalizza(o.label).includes(q) && normalizza(`${o.dettaglio || ''} ${o.value}`).includes(q));
        // Prima le voci con la parola nel nome, poi quelle che la hanno solo nei dettagli
        const tutti = [...nelNome, ...altrove];
        return massimo > 0 ? tutti.slice(0, massimo) : tutti;
    }, [opzioni, filtro, minCaratteri, massimo]);
    const sottoSoglia = minCaratteri > 0 && normalizza(filtro).trim().length < minCaratteri;

    const apri = () => {
        if (aperto) return;
        setTesto(scelta ? scelta.label : '');
        setAperto(true);
        const i = scelta ? opzioni.findIndex((o) => o.value === scelta.value) : 0;
        setAttiva(Math.max(0, i));
        // Il nome già scritto si seleziona: chi scrive lo sostituisce, chi usa le frecce lo modifica
        requestAnimationFrame(() => campo.current && campo.current.select());
    };
    const chiudi = () => { setAperto(false); setTesto(''); };
    const scegli = (o) => { onChange(o.value); chiudi(); campo.current && campo.current.blur(); };
    const svuota = () => {
        setTesto('');
        setAttiva(0);
        setAperto(true);
        campo.current && campo.current.focus();
    };

    // La voce evidenziata resta visibile scorrendo con le frecce
    useEffect(() => {
        if (!aperto || !elenco.current) return;
        const el = elenco.current.querySelector(`[data-indice="${attiva}"]`);
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [attiva, aperto]);

    const tasto = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); if (!aperto) apri(); else setAttiva((i) => Math.min(risultati.length - 1, i + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setAttiva((i) => Math.max(0, i - 1)); }
        else if (e.key === 'Enter') { if (aperto && risultati[attiva]) { e.preventDefault(); scegli(risultati[attiva]); } }
        else if (e.key === 'Escape') { if (aperto) { e.preventDefault(); chiudi(); campo.current && campo.current.blur(); } }
    };

    return (
        <div className={`sid-ricerca${aperto ? ' is-aperto' : ''}`}>
            <div className="sid-ricerca__campo" onMouseDown={(e) => { if (e.target !== campo.current) { e.preventDefault(); campo.current && campo.current.focus(); } }}>
                <svg className="sid-ricerca__lente" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" /><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                    ref={campo}
                    id={idCampo}
                    className="sid-ricerca__input"
                    type="text"
                    role="combobox"
                    aria-expanded={aperto}
                    aria-controls={idElenco}
                    aria-autocomplete="list"
                    aria-activedescendant={aperto && risultati[attiva] ? `${idElenco}-${attiva}` : undefined}
                    autoComplete="off"
                    value={aperto ? testo : (scelta ? scelta.label : '')}
                    // Chiuso, il nome occupa solo il suo spazio e accanto si leggono i dettagli
                    size={!aperto && scelta?.dettaglio ? Math.max(4, scelta.label.length) : undefined}
                    style={!aperto && scelta?.dettaglio ? { flex: '0 0 auto' } : undefined}
                    onFocus={apri}
                    onClick={apri}
                    onBlur={chiudi}
                    onChange={(e) => { setTesto(e.target.value); setAttiva(0); setAperto(true); }}
                    onKeyDown={tasto}
                />
                {!aperto && scelta?.dettaglio && <span className="sid-ricerca__dettaglio">{scelta.dettaglio}</span>}
                {aperto && testo && (
                    <button
                        type="button"
                        className="sid-ricerca__svuota"
                        aria-label={etichettaSvuota}
                        title={etichettaSvuota}
                        onMouseDown={(e) => { e.preventDefault(); svuota(); }}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 2.5l7 7m0-7l-7 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                    </button>
                )}
            </div>
            {aperto && (
                <ul ref={elenco} id={idElenco} className="sid-ricerca__elenco" role="listbox">
                    {risultati.length === 0 && <li className="sid-ricerca__vuoto">{sottoSoglia ? `Scrivi almeno ${minCaratteri} ${minCaratteri === 1 ? 'lettera' : 'lettere'}` : nessunRisultato}</li>}
                    {risultati.map((o, i) => (
                        <li
                            key={o.value}
                            id={`${idElenco}-${i}`}
                            data-indice={i}
                            role="option"
                            aria-selected={o.value === valore}
                            className={`sid-ricerca__voce${i === attiva ? ' is-attiva' : ''}${o.value === valore ? ' is-scelta' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); scegli(o); }}
                            onMouseEnter={() => setAttiva(i)}
                        >
                            <span className="sid-ricerca__etichetta"><Evidenzia testo={o.label} cerca={filtro} /></span>
                            {o.dettaglio && <span className="sid-ricerca__info"><Evidenzia testo={o.dettaglio} cerca={filtro} /></span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default CampoRicerca;
